import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import heroCoast from "../../assets/hero-play.webp";
import { fillYearsFromProfiles } from "@/content/staff-facts";
import { createFileRoute, Link } from "@tanstack/react-router";
import { heroCoastImage, heroCoastAvifSrc } from "@/lib/responsive-images";
import { getHiddenGate } from "@/lib/settings.functions";
import { staffPortraitsQuery, type StaffPortraitMap } from "@/lib/staff-portraits";
import { portraitBackground } from "@/lib/portrait-backgrounds";
import { staffProfilesQuery, type StaffProfileMap } from "@/lib/staff-profiles";
import { resolveSeo, siteContentQuery, useContent } from "@/lib/site-content";

import { CtaBand } from "../../components/site/Page";
import { HomeHero } from "@/components/site/HomeHero";
import { HiddenGateSlot } from "@/components/site/HiddenGateSlot";
import { HomeTeachers, HomeTrust, HomeWhyUs } from "@/components/site/HomeSections";
import { HomeHistory } from "@/components/site/HomeHistory";
import { HomeDeferredContent } from "@/components/site/HomeDeferredContent";
import { GuideLinks } from "../../components/site/Guides";
import { KnowledgeHubTeaser } from "../../components/site/KnowledgeHubTeaser";
import {
  ComparisonTable,
  EnrollButtons,
  OnboardingNote,
} from "../../components/site/Highlights";
import { ProofBand } from "@/components/site/ProofBand";
import { CampHomeFeature } from "@/components/site/CampHomeFeature";
import { MffBlock, TrustStrip } from "@/components/site/MffSection";

import { pathFor } from "../../lib/i18n";
import classroom from "../../assets/home-primary.webp";
import campus from "../../assets/campus-sign.webp";
import preschoolPlay from "../../assets/home-preschool.webp";
import preschoolClass from "../../assets/home-preschool-class.webp";
import schoolyard from "../../assets/schoolyard-basket.webp";
import football from "../../assets/profile-football-mff.webp";
import padel from "../../assets/profile-padel.webp";
import { Testimonials } from "@/components/site/Testimonials";
import { listPublishedTestimonials } from "@/lib/testimonials.functions";
import { PrincipalWord } from "@/components/site/PrincipalWord";
import { SchoolValues } from "@/components/site/SchoolValues";
import { safeLoader } from "@/lib/safe-loader";

export const Route = createFileRoute("/en/")({
  loader: async ({ context }) => {
    const [gate, data, portraitData, profileData, testimonials] = await Promise.all([
      safeLoader("en.home.hidden-gate", () => context.queryClient.ensureQueryData({ queryKey: ["hidden-gate"], queryFn: () => getHiddenGate() }), { enabled: false }),
      safeLoader("en.home.content", () => context.queryClient.ensureQueryData(siteContentQuery), undefined),
      // Porträttdata måste finnas redan vid serverrendering, annars laddas lärarbilderna sent.
      safeLoader("en.home.portraits", () => context.queryClient.ensureQueryData(staffPortraitsQuery), { portraits: {} }),
      safeLoader("en.home.profiles", () => context.queryClient.ensureQueryData(staffProfilesQuery), { profiles: {} }),
      // Omdömena hämtas redan på servern så sidan inte hoppar när de dyker upp.
      safeLoader("en.home.testimonials", () => context.queryClient.ensureQueryData({
        queryKey: ["testimonials", "published", "home", "en"],
        queryFn: () => listPublishedTestimonials({ data: { limit: 12, locale: "en", homeOnly: true } }),
      }), []),
    ]);
    const backgroundId = data?.content?.["settings"]?.["portrait_background"]?.en as string | undefined;
    return { hiddenGate: gate.enabled, seo: resolveSeo(data?.content, "start", "en"), profiles: profileData.profiles as StaffProfileMap, portraits: portraitData.portraits as StaffPortraitMap, backgroundId, testimonials };
  },
  head: ({ loaderData }) => {
    const title = loaderData?.seo.title ?? "";
    const description = loaderData?.seo.description ?? "";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: absoluteUrl("/en") },
        { name: "twitter:card", content: "summary_large_image" },
        ...ogImage("/og/start-v1.jpg"),
        { property: "og:locale", content: "en_GB" },
      ],
      links: [
        ...seoLinks("/en"),
        {
          rel: "preload",
          as: "image",
          type: "image/avif",
          href: heroCoastAvifSrc,
          imageSrcSet: heroCoastImage.avif,
          imageSizes: "100vw",
          fetchPriority: "high",
        } as never,
      ],
    };
  },
  component: Index,
});

const comparison = [
  {
    row: "Curriculum",
    us: "Swedish curriculum and Swedish syllabuses",
    other: "Spanish or international curriculum",
  },
  {
    row: "Language of instruction",
    us: "Swedish, with Spanish and English every day",
    other: "Spanish or English",
  },
  {
    row: "Teachers",
    us: "Swedish teaching qualification in all Swedish teaching",
    other: "Varies between schools",
  },
  {
    row: "On moving back to Sweden",
    us: "The pupil follows the same syllabus throughout",
    other: "Often adjustments and catch-up work needed",
  },
  {
    row: "Group size",
    us: "No more than 16 pupils per class (school year 2026/27)",
    other: "Often larger classes",
  },
  {
    row: "Secondary school and sixth form",
    us: "Swedish distance studies with on-site support",
    other: "Local system or IB",
  },
];

const program = [
  {
    to: pathFor("/forskola", "en") as never,
    image: preschoolPlay,
    alt: "Children building together at the table in the preschool",
    label: "3–5 years",
    title: "Preschool",
    text: "Play and learning at the centre, with the Swedish curriculum and natural practice in Spanish and English.",
  },
  {
    to: pathFor("/forskoleklass", "en") as never,
    image: preschoolClass,
    alt: "Preschool class children sitting at a low table practising numbers with pencils",
    label: "5–6 years",
    title: "Preschool class",
    text: "A gentle transition into school with play, language and social interaction at the centre.",
  },
  {
    to: pathFor("/grundskola", "en") as never,
    image: classroom,
    alt: "Pupils working in the school's classroom",
    label: "Reception–Year 6",
    title: "Primary school",
    text: "Swedish curriculum with a goal of Swedish-qualified teachers for all Swedish tuition and small groups.",
  },
  {
    to: pathFor("/fotbollsakademi", "en") as never,
    image: football,
    alt: "Football training on the grass pitch in Estepona",
    label: "Sports profile",
    title: "Malmö FF football academy",
    text: "Training following Malmö FF's methodology combined with studies in an international setting.",
  },
  {
    to: pathFor("/padel-tennis", "en") as never,
    image: padel,
    alt: "Padel court on the Costa del Sol",
    label: "Sports profile",
    title: "Padel & tennis",
    text: "Top-class training on the Costa del Sol, small groups and your own coach.",
  },
];

const steps = [
  { n: "1", title: "Submit an application", text: "Fill in the form – you'll see the fee right away." },
  { n: "2", title: "Visit the school", text: "Come in person or meet us online." },
  { n: "3", title: "Confirmation of a place", text: "We'll get back to you with a decision and start date." },
];

function Index() {
  const { text, list } = useContent("start");
  const { hiddenGate, profiles, portraits, backgroundId, testimonials } = Route.useLoaderData();
  const values = list("values");

  return (
    <>
      <HiddenGateSlot enabled={hiddenGate} />
      <HomeHero
        locale="en"
        eyebrow={text("hero.eyebrow")}
        title={text("hero.title")}
        intro={text("hero.intro")}
        primaryLabel={text("hero.primary_button")}
        secondaryLabel={text("hero.secondary_button")}
        note={text("hero.note")}
        imageAlt="Sunny schoolyard with chalk drawings, a skipping rope, a red ball and wooden toys under an olive tree"
      />

      <ProofBand locale="en" photos={list("proof.photos")} />

      <CampHomeFeature locale="en" />

      <TrustStrip locale="en" />

      <section id="varden" className="mx-auto max-w-6xl px-5 py-14 sm:py-20">
        <h2 className="text-2xl font-semibold sm:text-4xl">{text("values.title")}</h2>
        <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
          A small school with a Swedish foundation, in the middle of an international everyday
          life on the Costa del Sol.
        </p>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {values.map((v) => (
            <div key={v.title} className="rounded-xl border border-border bg-card p-6 shadow-soft">
              <h3 className="text-base font-semibold">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{fillYearsFromProfiles(v.text, profiles)}</p>
            </div>
          ))}
        </div>
      </section>

      <SchoolValues compact />

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-20">
        <h2 className="text-2xl font-semibold sm:text-4xl">The school and our profiles</h2>
        <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
          A school day that combines Swedish education with languages, culture and sport in
          southern Spain.
        </p>

        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          {program.map((item) => (
            <Link
              key={`${item.to}:${item.title}`}
              to={item.to}
              className="group overflow-hidden rounded-xl border border-border bg-card shadow-soft transition-colors hover:border-primary/40"
            >
              <img
                src={item.image}
                alt={item.alt}
                loading="lazy"
                className="h-56 w-full object-cover"
              />
              <div className="p-7">
                <p className="text-xs font-semibold tracking-[0.18em] text-primary uppercase">
                  {item.label}
                </p>
                <h3 className="mt-3 text-xl font-semibold">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                <span className="mt-4 inline-block text-sm text-primary group-hover:underline">
                  Read more
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <MffBlock locale="en" />

      <HomeTeachers locale="en" profiles={profiles} portraits={portraits} background={portraitBackground(backgroundId)} />

      <Testimonials title={text("testimonials.title")} items={list("testimonials")} locale="en" initialReviews={testimonials} />

      <HomeTrust locale="en" />

      <PrincipalWord locale="en" quote={text("principal.quote")} profiles={profiles} portraits={portraits} background={portraitBackground(backgroundId)} />

      <section className="border-y border-border bg-sand">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-20">
          <h2 className="text-2xl font-semibold sm:text-3xl">Swedish school or a local alternative?</h2>
          <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
            Both paths work – it depends on how long you're staying and what happens next.
            Here's how the setup differs in practice.
          </p>
          <ComparisonTable rows={comparison} locale="en" />
          <p className="mt-4 text-xs text-muted-foreground">
            The comparison is general. Provision varies between individual schools in the area –
            feel free to visit several before you decide.
          </p>
          <p className="mt-4">
            <Link
              to="/en/swedish-international-or-spanish-school"
              className="font-medium text-primary link-quiet"
            >
              Read the full comparison: Swedish, international or Spanish school
            </Link>
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl space-y-14 px-5 pt-12 pb-14 sm:space-y-20 sm:pt-16 sm:pb-20">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-semibold sm:text-3xl">Teaching with Swedish roots</h2>
            <ul className="mt-6 space-y-3 text-base leading-relaxed text-foreground/90">
              <li>Swedish curriculum, syllabuses and timetable – we aim for Swedish-qualified teachers in all Swedish tuition.</li>
              <li>Extended Spanish and English from reception year to Year 6.</li>
              <li>Written assessments in every year group up to and including Year 6.</li>
              <li>Digital tools – iPads and computers are part of learning.</li>
               <li>Small classes and mixed-age activities.</li>
            </ul>
          </div>
          <img
            src={campus}
            alt="Swedish International Schools sign at the school entrance"
            loading="lazy"
            className="rounded-xl object-cover shadow-soft"
            width={1600}
            height={1067}
          />
        </div>

        <div className="grid items-center gap-10 lg:grid-cols-2">
          <img
            src={schoolyard}
            alt="The school's outdoor yard with a basketball hoop and shading roof"
            loading="lazy"
            className="rounded-xl object-cover shadow-soft lg:order-2"
            width={1400}
            height={933}
          />
          <div>
            <h2 className="text-2xl font-semibold sm:text-3xl">Health, safety and community</h2>
            <ul className="mt-6 space-y-3 text-base leading-relaxed text-foreground/90">
              <li>Well-cooked school meals and healthy snacks every day.</li>
              <li>An outdoor yard for play and movement, including basketball and table tennis.</li>
              <li>A safe environment where every pupil is seen, both physically and psychosocially.</li>
              <li>Close teacher contact so parents can easily follow their children's studies.</li>
              <li>After-school care until 4:30pm with activities after the school day.</li>
            </ul>
            <Link
              to={pathFor("/gemenskap", "en") as never}
              className="mt-6 inline-block text-sm text-primary link-quiet"
            >
              How families meet at our school
            </Link>
          </div>
        </div>

      </section>

      <HomeWhyUs locale="en" />

      <section className="border-y border-border bg-sand">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <h2 className="text-2xl font-semibold">Three steps to starting school</h2>
          <ol className="mt-8 grid gap-4 sm:grid-cols-3 sm:gap-5">
            {steps.map((s) => (
              <li key={s.n} className="rounded-xl bg-card p-6 shadow-soft">
                <span className="font-display text-2xl font-semibold text-primary">{s.n}</span>
                <h3 className="mt-2 text-lg font-semibold">{s.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
              </li>
            ))}
          </ol>
          <EnrollButtons className="mt-8" locale="en" />
          <p className="mt-3 text-sm text-muted-foreground">
            Applications are accepted on a rolling basis – it's fine to start mid-term.
          </p>
          <OnboardingNote className="mt-8 bg-card" locale="en" />
        </div>
      </section>

      <HomeHistory locale="en" />

      <HomeDeferredContent locale="en" />

      <div className="pt-14 sm:pt-16">
        <KnowledgeHubTeaser locale="en" />
      </div>

      <div className="pt-14 sm:pt-16">
        <GuideLinks title="Guides for families moving here" locale="en" />
        <p className="mx-auto -mt-6 max-w-3xl px-5 pb-10 text-sm text-muted-foreground sm:pb-12">
          Want more detail? The Knowledge Hub explains each topic step by step.
        </p>
      </div>

      <CtaBand locale="en" />
      <div className="h-20" />
    </>
  );
}
